import { useAddScopesToAnalyzeFilters } from 'routes/BudgetModule/helpers/hooks/useAddScopesToAnalyzeFilters';
import { snakify } from 'helpers/syntaxHelper';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { transformUrlParams } from 'helpers/urlHelpers';
import {
    filterPostProcessingMiddleware,
    globalFiltersPostProcessingMiddleware,
} from 'routes/BudgetModule/components/shared/Filters/filtersMiddlewares';
import { trackEventBudget, TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import {
    UpdateFilters,
    getUpdatedFilters,
} from 'routes/BudgetModule/components/shared/Filters/helpers/filtersHelpers';

type UseBudgetFiltersT = {
    filters: SentenceFilterWithScopesT[];
};

/**
 * Custom hook to fetch global filters for the DAF view applicable to budgetFilters.
 * @returns An object containing the global filters and loading state.
 */
const useDafFiltersQuery = (
    bpId: number,
): {
    globalFilters: SentenceFilterWithScopesT[];
    isLoading: boolean;
} => {
    const { data: filters, isLoading } = useQuery({
        queryKey: ['budgetGlobalFiltersDAF', bpId],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                SentenceFilterWithScopesT[]
            >({
                endpoint: `budget_filters/${bpId}?is_overview=true`,
                method: 'GET',
                preProcessingUrlCallback: (url) =>
                    transformUrlParams(url, ['filter_field'], snakify),
                postProcessingCallback: globalFiltersPostProcessingMiddleware,
            });
            return response.content.data;
        },
    });
    const globalFilters = filters ?? [];

    const filtersWithScope = useAddScopesToAnalyzeFilters(globalFilters);

    return { globalFilters: filtersWithScope, isLoading };
};

type NewFilterBody = Pick<
    SentenceFilterT,
    'field' | 'operation' | 'type' | 'value'
>;

const preProcessingFilterBody = (newFilterBody: NewFilterBody) => {
    return { ...newFilterBody, field: snakify(newFilterBody.field) };
};

/**
 * Build and returns the react-query mutation that patches the budget filter settings.
 */
export const useMutateDafBudgetFilter = (
    bpId: number,
): { mutate: UpdateFilters } => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: NewFilterBody) =>
            RQAPICallWithData<NewFilterBody>({
                endpoint: 'budget_filters',
                method: 'PATCH',
                body,
                preProcessingBodyCallback: preProcessingFilterBody,
                postProcessingCallback: filterPostProcessingMiddleware,
            }),

        onSuccess: (data) => {
            queryClient.setQueryData(
                ['budgetGlobalFiltersDAF', bpId],
                (oldFilters: SentenceFilterT[]) => {
                    return getUpdatedFilters(oldFilters, data.content.data);
                },
            );
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetOverview.analyse_arguments
                    .saveFilter,
                {
                    field: data.content.data.field,
                    newValue: data.content.data.value,
                },
            );
        },
    });
};

/**
 * Custom hook to fetch budget user filters.
 * It uses integration software data to fetch relevant filters.
 * @returns An object containing the current budget filters.
 */
export const useGetDafBudgetFilters = (bpId: number): UseBudgetFiltersT => {
    const { globalFilters } = useDafFiltersQuery(bpId);

    return { filters: globalFilters };
};
